import { createSlice } from "@reduxjs/toolkit";

// Live global counters + presence pushed over the /ws/user socket. Previously the
// web never opened that socket, so the unread-message and notification badges
// never updated without a full refetch.
const initialState = {
  unreadCount: 0,
  unreadChatCount: 0,
  unreadRevision: 0,
  notificationCount: 0,
  notificationCountKnown: false,
  notificationRevision: 0,
  onlineFriends: [],
};

const userUpdatesSlice = createSlice({
  name: "userUpdates",
  initialState,
  reducers: {
    // New notifications use notification_update; the initial/safety refresh uses
    // batch_update. Both are real /ws/user frames, unlike pong/keepalive.
    // batch_update: { type, unread_count, notification_count, online_friends[], timestamp }
    receiveUserUpdate(state, action) {
      const frame = action.payload || {};
      const type = String(frame.type || "").toLowerCase();
      if (type !== "batch_update" && type !== "notification_update") {
        return;
      }
      if (type === "batch_update" && Number.isFinite(frame.unread_count)) {
        state.unreadCount = Math.max(0, frame.unread_count);
      }
      if (type === "batch_update" && Number.isFinite(frame.unread_chat_count)) {
        state.unreadChatCount = Math.max(0, frame.unread_chat_count);
        state.unreadRevision += 1;
      }
      if (
        typeof frame.notification_count === "number" &&
        Number.isFinite(frame.notification_count)
      ) {
        state.notificationCount = Math.max(0, frame.notification_count);
        state.notificationCountKnown = true;
        state.notificationRevision += 1;
      }
      if (type === "batch_update" && Array.isArray(frame.online_friends)) {
        state.onlineFriends = frame.online_friends;
      }
    },
    notificationReadConfirmed(state) {
      // The read endpoint does not broadcast an updated count. Keep its confirmed
      // result until the next authoritative socket snapshot arrives.
      if (state.notificationCountKnown) {
        state.notificationCount = Math.max(0, state.notificationCount - 1);
      }
    },
    syncChatUnreadCounts(state, action) {
      // The active chat page has a complete list plus live message updates.
      // Carry its count into the global badge when navigating to another page.
      if (Number.isFinite(action.payload?.messageCount)) {
        state.unreadCount = Math.max(0, action.payload.messageCount);
      }
      if (Number.isFinite(action.payload?.chatCount)) {
        state.unreadChatCount = Math.max(0, action.payload.chatCount);
        state.unreadRevision += 1;
      }
    },
    resetUserUpdates() {
      return initialState;
    },
  },
});

export const {
  receiveUserUpdate,
  notificationReadConfirmed,
  syncChatUnreadCounts,
  resetUserUpdates,
} = userUpdatesSlice.actions;
export default userUpdatesSlice.reducer;
